import qs from 'qs';
import axios from 'axios';
import { apiUrl } from '../utils/api.js';
import Autosuggest from 'react-autosuggest';
import escapeRegExp from 'lodash.escaperegexp';
import { useState, useRef, useEffect } from 'react';

const getSuggestionValue = suggestion => suggestion.name;

const renderSuggestion = ({ name }, { query }) => {
  const matchMatcher = new RegExp(escapeRegExp(query), 'i');

  const boldifiedName = {
    __html: name.replace(matchMatcher, '<b>$&</b>'),
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <span dangerouslySetInnerHTML={boldifiedName} />
      <svg width="8" height="12" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M7 6.7l.7-.7L2 .3.6 1.8 4.8 6 .6 10.2 2 11.7z"
          fill="#02DCC3"
          fillRule="nonzero"
        />
      </svg>
    </div>
  );
};

export function BankSelector({ label, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [value, setValue] = useState('');

  const canceller = useRef(() => {});

  function onSuggestionsFetchRequested({ value }) {
    canceller.current();

    axios
      .get(
        apiUrl('banks?' + qs.stringify({ filter: { searchQuery: value } })),
        {
          cancelToken: new axios.CancelToken(c => {
            canceller.current = c;
          }),
        }
      )
      .then(response => {
        setSuggestions(response.data.data);
      })
      .catch(error => {
        if (!axios.isCancel(error)) {
          throw error;
        }
      });
  }

  function onSuggestionsClearRequested() {
    setSuggestions([]);
  }

  function onSuggestionSelected(event, { suggestion }) {
    setIsOpen(false);

    onChange(suggestion);
  }

  function onInputChange(event, { newValue }) {
    setValue(newValue);
  }

  return (
    <div className={`root ${isOpen ? 'root--is-open' : ''}`}>
      <label className="label" htmlFor="searchable-select-input">
        {label}
      </label>
      <Autosuggest
        suggestions={suggestions}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        onSuggestionSelected={onSuggestionSelected}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={renderSuggestion}
        inputProps={{
          id: 'searchable-select-input',
          placeholder: 'Skriv bankens navn',
          value,
          onChange: onInputChange,
          onFocus: e => {
            if (
              window.matchMedia &&
              !window.matchMedia('(min-width: 750px)').matches
            ) {
              window.scrollTo(0, 0);
              document.body.scrollTop = 0;
            }

            setIsOpen(true);
          },
        }}
      />
      <style jsx>{`
        .label {
          display: block;
          margin-bottom: 9px;
          font-weight: 600;
          color: var(--bgBlueish__text);
        }

        .root--is-open {
          padding: 18px;
          position: fixed;
          z-index: 100;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          background-color: var(--bgBlueish);
        }

        .root :global(.react-autosuggest__input) {
          width: 100%;
          box-sizing: border-box;
          height: 50px;
          padding: 0 12px;
          margin-bottom: 18px;
          background-color: var(--bgBlueish);
          color: var(--bgBlueish__text);
          box-shadow: none;
          appearance: none;
          border: 3px solid var(--bgBlueish__fadedText);
          font-size: 16px;
          font-weight: 500;
          border-radius: 4px;
          line-height: normal;
        }

        .root :global(.react-autosuggest__input--focused) {
          outline: none;
          border-color: var(--bgBlueish__tintedText);
        }

        .root :global(.react-autosuggest__suggestions-container) {
          display: none;
          box-sizing: border-box;
        }

        .root :global(.react-autosuggest__suggestions-container--open) {
          display: block;
          font-weight: 500;
          font-size: 16px;
        }

        .root :global(.react-autosuggest__suggestions-list) {
          margin: 0;
          padding: 0;
          list-style-type: none;
        }

        .root :global(.react-autosuggest__suggestion) {
          padding: 9px 12px;
          margin-bottom: 12px;
          background-color: var(--bgHotBlue);
          border-radius: 3px;
          color: var(--bgHotBlue__text);
          font-weight: 600;
        }

        @media screen and (min-width: 750px) {
          .root--is-open {
            padding: 0;
            position: static;
            top: auto;
            right: auto;
            bottom: auto;
            left: auto;
            background-color: transparent;
          }

          .root :global(.react-autosuggest__container) {
            position: relative;
          }

          .root :global(.react-autosuggest__input--open) {
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
          }

          .root :global(.react-autosuggest__suggestions-container--open) {
            border: 3px solid var(--bgBlueish__fadedText);
            border-top: none;
            background-color: var(--bgBlueish);
            border-bottom-left-radius: 4px;
            border-bottom-right-radius: 4px;
            z-index: 2;
            position: absolute;
            top: 50px;
            width: 100%;
          }

          .root :global(.react-autosuggest__suggestion) {
            cursor: pointer;
            padding: 10px 20px;
            margin-bottom: 0;
            background-color: var(--bgBlueish);
            border-radius: 0;
            color: var(--bgBlueish__text);
          }

          .root :global(.react-autosuggest__suggestion path) {
            fill: var(--bgBlueish__text);
          }

          .root :global(.react-autosuggest__suggestion--highlighted) {
            background-color: var(--bgBlueish__fadedText);
          }
        }
      `}</style>
    </div>
  );
}
